// Copyright 2019 Sorama B.V.

import { BindingEngine, Disposable } from "aurelia-binding";
import { autoinject, transient } from "aurelia-framework";
import { TechnicalTroubleshootingGuidanceCatalog } from "catalogs/GuidanceCatalog/TechnicalTroubleshootingGuidanceCatalog";
import { ClientWorkflowModuleCatalog } from "catalogs/WorkflowModuleCatalog/ClientWorkflowModuleCatalog";
import { HelpWorkflowModuleCatalog } from "catalogs/WorkflowModuleCatalog/HelpWorkflowModuleCatalog";
import { MeasurementWorkflowModuleCatalog } from "catalogs/WorkflowModuleCatalog/MeasurementModuleCatalog";
import { IAuthentication } from "libraries/Authentication/IAuthentication";
import { OpenIDUnauthorizedNavigationStrategy } from "libraries/Authentication/OpenIDUnauthorizedNavigationStrategy";
import { ShellViewManager } from "modules/shell/App/ShellViewManager";
import { ClientStatus, IClientService } from "services/Client/IClientService";
import { ILicenseService } from "services/License/ILicenseService";
import { IServerStatusService, ServerStatus } from "services/Server/IServerStatusService";
import { WorkflowModeService } from "services/WorkflowModes/WorkflowModeService";
import { IWorkflowModuleConfig } from "../Interfaces/IWorkflowModuleConfig";
import { SidebarRouter } from "../Sidebar/SidebarRouter";

export class WorkflowModuleRequirementsAction {
    public message: string;
    public clickAction?: () => void;
    public clickActionText?: string;
}

@autoinject()
@transient()
export class WorkflowModuleRequirements {
    public actions: WorkflowModuleRequirementsAction[] = [];

    private observables: Disposable[] = [];

    constructor(
        private bindingEngine: BindingEngine,
        private readonly authentication: IAuthentication,
        private readonly clientService: IClientService,
        private readonly serverService: IServerStatusService,
        private readonly shellViewManager: ShellViewManager,
        private readonly licenseService: ILicenseService,
        private readonly unauthorizedStrategy: OpenIDUnauthorizedNavigationStrategy,
        private readonly workflowModeService: WorkflowModeService
    ) {}

    public subscribeModule(moduleConfig: IWorkflowModuleConfig): void {
        this.observables = [
            this.bindingEngine.propertyObserver(this.authentication, "isSignedIn").subscribe(() => {
                this.checkModule(moduleConfig);
            }),
            this.bindingEngine.propertyObserver(this.serverService, "status").subscribe(() => {
                this.checkModule(moduleConfig);
            }),
            this.bindingEngine.propertyObserver(this.clientService, "status").subscribe(() => {
                this.checkModule(moduleConfig);
            }),
            this.bindingEngine.propertyObserver(this.workflowModeService, "selectedWorkflowMode").subscribe(() => {
                this.checkModule(moduleConfig);
            }),
        ];

        this.checkModule(moduleConfig);
    }

    public unsubscribeModule(): void {
        for (const disposable of this.observables) {
            disposable.dispose();
        }

        this.observables = [];
    }

    public checkModule(moduleConfig: IWorkflowModuleConfig): void {
        const result: WorkflowModuleRequirementsAction[] = [];

        if (moduleConfig.requireSignIn && !this.authentication.isSignedIn)
            result.push({
                message: "You are not signed in",
                clickAction: () => {
                    this.signIn();
                },
                clickActionText: "Click to sign in",
            });

        if (moduleConfig.requireOnline && this.serverService.status !== ServerStatus.Online)
            result.push({ message: "This module is not available in offline mode" });

        if (moduleConfig.requireClient && this.clientService.status === ClientStatus.NotAvailable)
            result.push({
                message: "Cannot connect to the acquisition service",
                clickAction: () => {
                    this.showClientConnectionHelp();
                },
                clickActionText: "Help me fix this",
            });

        if (moduleConfig.requireClient && this.clientService.status === ClientStatus.UnsupportedVersion)
            result.push({
                message: "You are using an outdated version of the acquisition service which is no longer supported",
                clickAction: () => {
                    this.showClientUpdate();
                },
                clickActionText: "Click here to update the acquisition service",
            });

        if (
            moduleConfig.disableForWorkflowModes &&
            moduleConfig.disableForWorkflowModes.includes(this.workflowModeService.selectedWorkflowMode)
        ) {
            result.push({ message: "Your selected workflow mode does not include this feature" });
        }

        this.actions.splice(0, this.actions.length, ...result);
    }

    public signIn(): void {
        // Redirect to the unauthorized page for sign in
        this.unauthorizedStrategy.navigate();
    }

    public checkServerStatus(): void {
        this.serverService.updateStatus();
    }

    public showClientConnectionHelp(): void {
        this.shellViewManager.openRightSideBar(HelpWorkflowModuleCatalog.HelpModule, [
            {
                prefix: SidebarRouter.prefix,
                name: "topicName",
                value: TechnicalTroubleshootingGuidanceCatalog.TechnicalTroubleshootingGS2Module.name,
            },
        ]);
    }

    public showClientUpdate(): void {
        this.shellViewManager.openRightSideBar(ClientWorkflowModuleCatalog.ClientUpdateModule);
    }

    public showHardwareStatus(): void {
        this.shellViewManager.openRightSideBar(MeasurementWorkflowModuleCatalog.hardwareSelectionModule);
    }
}
